import type { PlcCounters } from "../../../contracts/plc-counters.js";
import type { PlcMonitorFields } from "../../../contracts/plc-monitor.js";
import type { CompletedCasesReading } from "../../../contracts/completed-cases.js";

export type LiveCounter = PlcMonitorFields & {
  completedCases?: CompletedCasesReading | null;
  plcCounters?: PlcCounters | null;
  configured: boolean;
  deviceId: string | null;
  unit: string | null;
  value: number | null;
  runQuantity: number | null;
  persistedProductionQuantity?: number | null;
  baselineAt?: string | null;
  machineRunning?: boolean | null;
  machineMeasuredAt?: string | null;
  m0ControlEnabled?: boolean;
  machineRunVersion?: number | null;
  packagingIssue?: string | null;
  speedPerMinute?: number | null;
  speedMeasuredAt?: string | null;
  measuredAt: string | null;
  receivedAt: string | null;
  quality: "GOOD" | "STALE" | "COUNTER_RESET" | "BASELINE_MISSING" | "GAP_ACROSS_RUN" | "NO_DATA";
  source?: "LOCAL_PLC_TEST";
  counterIssue?: string | null;
  packaging?: { boxesPerCase: number; source: "MIKRO_ERP" | "OPERATOR_CONFIRMED"; observedAt: string } | null;
};

export function machineCountLabel(lineName: string): "ürün" | "kutu" | "silindir" {
  const normalized = lineName.trim().normalize("NFKD").replace(/\p{M}/gu, "").toUpperCase().replace(/\s+/g, " ");
  if (normalized === "TAMTAS SILINDIR") return "silindir";
  return /^ELLE AMBALAJ(?:\s|$)/iu.test(lineName.trim()) ? "ürün" : "kutu";
}

export function plannedBoxCount(counter: LiveCounter | null, plannedQuantity: number | null, planUnit: string): number | null {
  const factor = counter?.packaging?.boxesPerCase;
  if (planUnit.trim().toLocaleUpperCase("tr-TR") !== "KOLİ" || plannedQuantity == null ||
      !Number.isFinite(plannedQuantity) || plannedQuantity < 0 || factor == null ||
      !Number.isSafeInteger(factor) || factor <= 0) return null;
  const boxes = plannedQuantity * factor;
  return Number.isSafeInteger(boxes) ? boxes : null;
}

// Bench-only assumption: one D100 trigger represents one box. For the local
// pilot, only run-baselined, persisted production quantity is shown here.
export function machineBoxCount(counter: LiveCounter | null, warning: boolean): number | null {
  if (!counter || warning) return null;
  const count = counter.source === "LOCAL_PLC_TEST" ? (counter.quality === "GOOD" ? counter.runQuantity : null) :
    counter.quality === "GOOD" && counter.unit?.toLocaleUpperCase("tr-TR") === "KUTU" ? counter.runQuantity : null;
  return count != null && Number.isSafeInteger(count) && count >= 0 ? count : null;
}

export function averageRunRatePerMinute(quantity: number | null, elapsedMilliseconds: number | null): number | null {
  if (quantity == null || !Number.isFinite(quantity) || quantity < 0 ||
      elapsedMilliseconds == null || !Number.isFinite(elapsedMilliseconds) || elapsedMilliseconds < 60_000) return null;
  return quantity * 60_000 / elapsedMilliseconds;
}

export function machineSpeedPresentation(counter: LiveCounter | null, unavailable: boolean, now: number) {
  if (unavailable) return { value: null, label: "Makine bağlantısı kontrol edilmeli" };
  const value = counter?.speedPerMinute;
  const measured = Date.parse(counter?.speedMeasuredAt ?? "");
  if (!counter?.configured || value == null || !Number.isSafeInteger(value) || value < 0 || value > 0xffffffff || !Number.isFinite(measured)) {
    return { value: null, label: "Makine hız ölçümü bekleniyor" };
  }
  if (now - measured > 10_000 || measured - now > 10_000) return { value: null, label: "Makine hız ölçümü gecikti" };
  return { value, label: "PLC'nin son dakika ölçümü" };
}

export function counterPresentation(counter: LiveCounter | null, unavailable: boolean, now: number) {
  if (!counter) return { label: unavailable ? "Sayaç bağlantısı kontrol edilemiyor" : "Sayaç kontrol ediliyor…", warning: unavailable, ageSeconds: null };
  if (!counter.configured) return { label: "Bu makineye sayaç bağlanmamış", warning: false, ageSeconds: null };
  const measured = Date.parse(counter.measuredAt ?? "");
  const ageSeconds = Number.isFinite(measured) ? Math.max(0, Math.floor((now - measured) / 1000)) : null;
  const stale = ageSeconds != null && ageSeconds > 10;
  if (counter.source === "LOCAL_PLC_TEST") return {
    label: unavailable ? "Makine bağlantısı kesildi" : counter.counterIssue ?? (counter.value == null ? "Makine sayacı okunamıyor" : stale ? "Makine ölçümü gecikti" : "Makineden veri alınıyor"),
    warning: unavailable || stale || counter.value == null || counter.quality !== "GOOD",
    ageSeconds,
  };
  const messages: Record<LiveCounter["quality"], string> = {
    GOOD: "Veri alınıyor",
    STALE: "Sayaç verisi gecikiyor",
    COUNTER_RESET: "Sayaç sıfırlandı veya taştı; iş toplamı doğrulanamadı",
    BASELINE_MISSING: "İş başlangıcı ölçülmedi; iş toplamı doğrulanamadı",
    GAP_ACROSS_RUN: "Ölçüm aralığı doğrulanamadı; iş toplamı doğrulanamadı",
    NO_DATA: "Cihazdan ilk veri bekleniyor",
  };
  return {
    label: unavailable ? "Sunucu bağlantısı kesildi · son ölçüm gösteriliyor" : stale ? "Sayaç verisi gecikiyor" : messages[counter.quality],
    warning: unavailable || stale || !["GOOD", "NO_DATA"].includes(counter.quality),
    ageSeconds,
  };
}

export function counterSyncLabel(counter: LiveCounter | null, status: ReturnType<typeof counterPresentation>): string | null {
  if (!counter?.configured) return null;
  if (status.warning || counter.quality !== "GOOD") return status.label;
  if (counter.source === "LOCAL_PLC_TEST") return "Yerel makine ölçümü · Sunucu aktarımı doğrulanmadı";
  const received = Date.parse(counter.receivedAt ?? "");
  const time = Number.isFinite(received)
    ? new Intl.DateTimeFormat("tr-TR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Istanbul" }).format(received)
    : "Zaman bilgisi yok";
  return `Sunucudaki son sayaç ölçümü · ${time}`;
}
